/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { environment } from '../../../environments/environment'
import { AppConfig } from './config.interface'
import { MyProfileConfigLoaderService } from './my-profile-config-loader.service'
import { Observable, of, ReplaySubject, tap, take } from 'rxjs'
import { WINDOW } from '@myprofile-ui/pidx-utils'

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private configLoader = inject(MyProfileConfigLoaderService)
  private _window = inject<Window>(WINDOW)

  protected config: AppConfig = {}
  protected country = ''
  protected locale = ''
  protected _initialized = new ReplaySubject<void>(1)

  initialized = this._initialized.asObservable().pipe(take(1))

  init(locale: string, country: string): Observable<AppConfig> {
    this.locale = locale
    this.country = country

    return this.loadConfig(locale, country).pipe(
      tap(() => {
        this._initialized.next()
      }),
    )
  }

  setUrlLocaleCountryInformation(locale: string, country: string) {
    this.country = country
    this.locale = locale
  }

  getUrlLocale(): string {
    return this.locale
  }

  getUrlCountry(): string {
    return this.country
  }

  getTranslationFileName(): string {
    return this.config?.localeInformation?.translationFileName ?? ''
  }

  getFallbackTranslationFileName(): string {
    return this.config?.localeInformation?.fallbackTranslationFileName ?? ''
  }

  getAngularLocale(): string {
    return this.config?.localeInformation?.angularLocale ?? ''
  }

  getLocaleInformation(): LocaleInformation {
    return this.config?.localeInformation ?? {}
  }

  getFeatureToggles(): FeatureToggles {
    return this.config?.featureToggles ?? {}
  }

  private loadConfig(locale: string, country: string): Observable<AppConfig> {
    if (this.configLoader !== null) {
      return this.configLoader.getConfig(locale, country).pipe(
        tap((config: AppConfig) => {
          this.config = config
        }),
      )
    }

    return of({} as AppConfig)
  }

  getBaseUrl(): string {
    return environment.baseUrl
  }

  get getTranslationsUrl(): string {
    const STAGE = environment.production ? 'RELEASE' : 'LATEST'
    return `${environment.publicApi.localisation}/translations/${STAGE}/myprofile/`
  }

  get getTranslationsUrlSharedWebComponents(): string {
    const STAGE = environment.production ? 'RELEASE' : 'LATEST'
    return `${environment.publicApi.localisation}/translations/${STAGE}/shared-webcomponents/`
  }

  getPublicApiConfig(): { baseUrl: string } {
    return environment.publicApi
  }

  getAppBaseUrl(): string {
    return `${environment.baseUrl}${environment.environmentUrlAddition}`
  }

  getHomeUrl(): string {
    return `${this.getAppBaseUrl()}/${this.getUrlCountry()}/${this.getUrlLocale()}`
  }

  getStaticAssetsUrl(assetName: string, path: string[] = ['images']): string {
    const pathSegments = [this.getAppBaseUrl(), 'assets']
    pathSegments.push(...path)

    return [...pathSegments, assetName].join('/')
  }

  getHeaderLinks(): HeaderExternalLink[] {
    return this.config?.NAVIGATION ?? []
  }

  getExternalUrl(parameters: { identifier: string; country?: string; language?: string; suffix?: string }): string {
    const { identifier, country, language, suffix } = parameters
    const baseUrl = (environment.externalUrls as Record<string, string> | undefined)?.[identifier]

    if (!baseUrl) {
      return ''
    }

    let result = baseUrl

    if (country) {
      result += `/${country}`
    }

    if (language) {
      result += `/${language}`
    }

    if (suffix) {
      result += `/${suffix}`
    }

    return result
  }

  isProduction(): boolean {
    return environment.production
  }

  getPaymentWidgetConfig() {
    return {
      isProd: environment.production,
      view: environment.payment.view,
      apiKey: environment.payment.apiKey,
    }
  }

  getPaymentWidgetScriptUrl(): string {
    return environment.payment.scriptUrl
  }

  getGTMApplicationID(): string {
    return environment.googleTagManagerV2.data.context.applicationId
  }

  getGTMEnvironment(): string {
    return environment.googleTagManagerV2.data.context.environment
  }

  getGTMIsEnabled(): boolean {
    return environment.googleTagManagerV2.enable && !this.config.isTestAccount
  }

  getGTMContainerID(): string {
    return environment.googleTagManagerV2.containerId
  }

  isImpersonatedEditMode(): boolean {
    return this.config?.isImpersonatedEditMode ?? false
  }

  isCNDomain(): boolean {
    return this.getHostname().includes('.cn')
  }

  getHostname(): string {
    return this._window.location.hostname
  }
}
export interface HeaderExternalLink {
  key?: string
  text?: string
  url?: string
}
export interface FeatureToggles {
  disabled?: Array<string>
  enabled?: Array<string>
}
export interface LocaleInformation {
  angularLocale?: string
  fallbackTranslationFileName?: string
  iso6391Language?: string
  momentLocale?: string
  translationFileName?: string
}
